import React from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
import { PageHeader, PageSection, PageSubSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, SecondaryCallout, KMCurve, ComparisonTable, EndpointDisplay, ReferencesBlock ,TextBlock, PrimaryCallOut} from '../../components';
import { StaticImage } from 'gatsby-plugin-image';
import * as colors from '../../util/colors';
import ESO1 from '../../components/StudyDesign/__study-design/eso1'
import ESO2 from '../../components/StudyDesign/__study-design/eso2'
import ESO3 from '../../components/StudyDesign/__study-design/eso3'
import ESO4 from '../../components/StudyDesign/__study-design/eso4'
import { jobCodes } from '../../util/data/jobCodes';
import { Heading } from '../../util/typography';
import styled from 'styled-components';
import { createMarkup } from '../../util/functions';

// SEO INFORMATION
const pageMeta = {
    title: `KEYNOTE-590, KEYNOTE-180 & KEYNOTE-181 - Clinical Trial Results | HCP`,
    keywords: `keynote 590, keynote 180, keynote 181`,
    description: `Health care professionals can find clinical trial results for KEYNOTE-590, KEYNOTE-180, and KEYNOTE-181 in patients with advanced esophageal carcinoma or gej cancer.`,
    schemaJsonLD: [
        `{"@context":"http://schema.org/","url":"https://www.keytrudahcp.com/efficacy/esophageal-carcinoma/","@type":"MedicalTrial","name":"KEYNOTE⁠-⁠590","description":"1L Combination Therapy Clinical Findings from KEYNOTE⁠-⁠590","trialDesign":"The efficacy of KEYTRUDA was investigated in KEYNOTE⁠-⁠590, a multicenter, randomized, placebo-controlled trial that enrolled 749 patients with metastatic or locally advanced esophageal or gastroesophageal junction (tumors with epicenter 1 to 5 centimeters above the GEJ) carcinoma who were not candidates for surgical resection or definitive chemoradiation."}`,
        `{"@context":"http://schema.org/","url":"https://www.keytrudahcp.com/efficacy/esophageal-carcinoma/","@type":"MedicalTrial","name":"KEYNOTE⁠-⁠181","description":"Study Design for KEYNOTE⁠-⁠181","trialDesign":"The efficacy of KEYTRUDA was investigated in KEYNOTE⁠-⁠181, a multicenter, randomized, open-label, active-controlled trial that enrolled 628 patients with recurrent locally advanced or metastatic esophageal cancer who progressed on or after 1 prior line of systemic treatment for advanced disease."}`,
        `{"@context":"http://schema.org/","url":"https://www.keytrudahcp.com/efficacy/esophageal-carcinoma/","@type":"MedicalTrial","name":"KEYNOTE⁠-⁠180","description":"Clinical Findings from KEYNOTE⁠-⁠180","trialDesign":"The efficacy of KEYTRUDA was investigated in KEYNOTE⁠-⁠180, a multicenter, nonrandomized, open-label trial that enrolled 121 patients with locally advanced or metastatic esophageal cancer who progressed on or after at least 2 prior systemic treatments for advanced disease. With the exception of the number of prior lines of treatment, the eligibility criteria were similar to and the dosage regimen identical to KEYNOTE⁠-⁠181."}`,
        `{"@context":"http://schema.org/","url":"https://www.keytrudahcp.com/efficacy/esophageal-carcinoma/","@type":"MedicalAudience","audienceType":"Health Care Professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`
    ]
}

//from /src/util/data/indications.js
const indicationId = 19;
const jobCode = jobCodes[19].jobCode;

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections: [
        {
            heading: "Additional Clinical Data",
            items: [
                { text: '1L Combo Therapy - Advanced HER2-Negative Gastric or GEJ Adenocarcinoma', url: '/efficacy/gastric-adenocarcinoma/' },
                { text: 'Upper GI NCCN Recommendations ', url: '/static/pdf/Upper-GI-NCCN.pdf', target: '_blank' },

            ]
        },
        {
            heading: "Related Information",
            items: [
                { text: 'Selected Adverse Reactions', url: '/safety/adverse-reactions/esophageal-carcinoma/' },
                { text: 'Dosing', url: '/dosing/options/' },
                { text: 'PD&#8288;-&#8288;L1 Testing & Scoring', url: '/biomarker-testing/pd-l1/' },
            ]
        },
    ],
    interestLinks: {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
};

const anchorLinkModelData = [
    { label: ' 1L Combination Therapy Clinical Findings from KEYNOTE-590' },
    { label: 'Study Design for KEYNOTE⁠&#8288;-&#8288;590'},
    { label: '2L or Greater Monotherapy Clinical Findings from KEYNOTE-180 and KEYNOTE-181'},
    { label: 'Study Design for KEYNOTE-181'},
    { label: 'Study Design for KEYNOTE-180'}
]

const keynoteFiveNineOhFootnotes = [
    {
        label: "a.",
        text: "Based on the stratified Cox proportional hazard model."
    },
    // {
    //     label: "b.",
    //     text: "Investigator’s choice of 5-FU plus cisplatin (FP) or oxaliplatin plus capecitabine (CAPOX). "
    // },
    
]
const esokeynotekmcurvetwo = [
    {
        label: "a.",
        text: "Based on the stratified Cox proportional hazard model."
    },
    {
        label: "b.",
        text: "Based on stratified log-rank test."
    },
    
]
const esokeynotecomparison = [
    {
        label: "a.",
        text: "Based on the stratified Cox proportional hazard model."
    },
    {
        label: "b.",
        text: "Based on a stratified log-rank test."
    },
    {
        label: "c.",
        text: "Confirmed complete response or partial response."
    },    
]

const keynoteOneEightOneFootnotes = [
    {
        label: "d.",
        text: `Based on the Cox regression model stratified by geographic region (Asia vs ex-Asia).`
    },
]

const overallSurvivalOneEightOneTable = {
    data: [
        [
            {
                legendTitle: 'KEYTRUDA (n=85)',
            },
            {
                legendTitle: 'Chemotherapy (n=82)',
                legendSwatchColor: colors.concrete,
            },
        ],
        [
            {
                colspan: 2,
                centerText: true,
                hrCiP: 'HR<sup>d</sup>=0.64; 95% CI, 0.46–0.90',
            }
        ],
        [
            {
                label: 'Events Observed',
                dataPoint: '80%',
                dataSubPoint: '(68/85)',
            },
            {
                label: 'Events Observed',
                dataPoint: '88%',
                dataSubPoint: '(72/82)',
            },
        ],
        [
            {
                label: 'Median OS',
                dataPoint: '10.3 months',
                dataSubPoint: '(95% CI, 7.0–13.5)',
            },
            {
                label: 'Median OS',
                dataPoint: '6.7 months',
                dataSubPoint: '(95% CI, 4.8–8.6)',
            },
        ],
    ],
    footnotes: [
        {
            label: "d.",
            text: "Based on the Cox regression model stratified by geographic region (Asia vs ex-Asia)."
        },
    ],
}

const progressiveFreeSurvivalOneEightOneTable = {
    data: [
        [
            {
                legendTitle: 'KEYTRUDA (n=85)',
            },
            {
                legendTitle: 'Chemotherapy (n=82)',
                legendSwatchColor: colors.concrete,
            },
        ],
        [
            {
                colspan: 2,
                centerText: true,
                hrCiP: 'HR=0.66; 95% CI, 0.48–0.92<sup>d</sup>',
            }
        ],
        [
            {
                label: 'Median PFS',
                dataPoint: '3.2 months',
                dataSubPoint: '(95% CI, 2.1–4.4)',
            },
            {
                label: 'Median PFS',
                dataPoint: '2.3 months',
                dataSubPoint: '(95% CI, 2.1–3.4)',
            },
        ],
        [
            {
                label: 'Events observed',
                dataPoint: '89%',
                dataSubPoint: '(n=76)',
            },
            {
                label: 'Events observed',
                dataPoint: '93%',
                dataSubPoint: '(n=76)',
            },
        ],
    ],
    footnotes: [
        {
            label: "d.",
            text: "Based on the Cox regression model stratified by geographic region (Asia vs ex-Asia)."
        },
    ],
}

const ObjectiveResponsRatelOneEightOneTable = {
    data: [
        [
            {
                legendTitle: 'KEYTRUDA (n=85)',
            },
            {
                legendTitle: 'Chemotherapy (n=82)',
                legendSwatchColor: colors.concrete,
            },
        ],
        [
            {
                label: 'ORR',
                dataPoint: '22%',
                dataSubPoint: '(95% CI, 14–33)',
            },
            {
                label: 'ORR',
                dataPoint: '7%',
                dataSubPoint: '(95% CI, 3–15)',
            },
        ],
        [
            {
                label: 'Complete Response',
                dataPoint: '5%',
                dataSubPoint: '(n=4)'
            },
            {
                label: 'Complete Response',
                dataPoint: '1%',
                dataSubPoint: '(n=1)'
            },
        ],
        [
            {
                label: 'Partial Response',
                dataPoint: '18%',
                dataSubPoint: '(n=15)'
            },
            {
                label: 'Partial Response',
                dataPoint: '6%',
                dataSubPoint: '(n=5)'
            },
        ],
    ]
}
const ORRendpointDataFootnotes = [
    {
        label: 'h.',
        text: 'Based on the stratified Miettinen and Nurminen method.'
    },
    {
        label: 'i.',
        text: 'Confirmed complete response or partial response.'
    }
]

const DOR181ComparisonTableData = {
    data: [
        [
            {
                legendTitle: 'KEYTRUDA (n=85)',
            },
            {
                legendTitle: 'Chemotherapy (n=82)',
                legendSwatchColor: colors.concrete,
            },
        ],
        [
            {
                label: 'Median DOR',
                dataPoint: '9.3 months',
                dataSubPoint: '(range: 2.1+ – 18.8+ months)',
            },
            {
                label: 'Median DOR',
                dataPoint: '7.7 months',
                dataSubPoint: '(range: 4.3–16.8+ months)',
            },
        ]
    ],
    definitions: "+ Denotes ongoing response."
}

const pageReferences = [
    {
        label: "1.",
        text: "Sun JM, Shen L, Shah MA, et al. Pembrolizumab plus chemotherapy versus chemotherapy alone for first-line treatment of advanced oesophageal cancer (KEYNOTE-590): a randomised, placebo-controlled, phase 3 study. <i>Lancet.</i> 2021;398(10302):759-771."
    }
];

const secondaryCalloutHeading = 'Patients with PD-L1 CPS ≥10 (n=383)'

const keynoteFiveNineOhDefinition = `1L = first line; 5-FU = 5-fluorouracil; CI = confidence interval; CPS = combined positive score; GEJ = gastroesophageal junction; HER2 = human epidermal growth factor receptor 2; HR = hazard ratio; ITT = intent-to-treat; OS = overall survival; PD⁠-⁠L1 = programmed death ligand 1; PFS = progression-free survival.`

const SecondaryCalloutWrapper = styled.div`
> div {
   margin-top: calc(14.10px + 0.60%);
}
`;

const TextBlockWrapper = styled.div`
margin-top: -10px`;

const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={pageMeta} location={location}>
            <div data-template-name="template-e">

                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Clinical Trial Results" />
                    <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId} />
                        <AnchorLinks items={anchorLinkModelData} />
                    </PageSection>
                    <PageSection bgColor="gradient">
                        <PrimaryCallOut
                            title="Clinically Meaningful Improvement in OS for 1L Treatment With KEYTRUDA + Chemotherapy vs Chemotherapy Alone in Advanced Esophageal or GEJ Carcinoma (PD-L1 Expressors)"
                        />
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                    {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        <PageSection title={anchorLinkModelData[0].label} bgColor="cloud">
                            <PageSubSection >
                             <SecondaryCalloutWrapper>
                                <Heading.H4 as='H3' dangerouslySetInnerHTML={createMarkup('Patients with PD-L1 CPS ≥1 (n=647)')} />
                                <SecondaryCallout
                                title="29% reduction in the risk of death with KEYTRUDA + chemotherapy vs chemotherapy alone"
                                alt=""
                            />
                            </SecondaryCalloutWrapper>                               
                                
                            
                            <KMCurve
                                title="Kaplan-Meier curve for OS in patients with PD⁠&#8288;-&#8288;⁠L1 CPS ≥1"
                                image={() => <StaticImage 
                                    placeholder="blurred"
                                    src="../../assets/KEYNOTE-590_KM_Curve-v3.png"
                                    alt="KEYNOTE-590: Kaplan-Meier Estimates of OS With KEYTRUDA® (pembrolizumab) + Cisplatin/FU vs Cisplatin/FU Alone in Patients With PD-L1 CPS ≥1" />}
                                hr="HR<sup>a</sup>=0.71 (95% CI, 0.59–0.84)"
                                additionalInformation={[
                                    {
                                        // title: 'Additional Information',
                                        text: `
                                            <p>Median OS in months (95% CI):
                                                12.7 (10.5&ndash;14.4) with KEYTRUDA + cisplatin/FU vs 9.8 (8.8&ndash;10.8) with cisplatin/FU alone (HR<sup>a</sup>=0.71; 95% CI, 0.59&ndash;0.84).
                                                </p>
                                            <p>Events observed:
                                                69% (222/320) with KEYTRUDA + cisplatin/FU vs 83% (271/327) with cisplatin/FU alone. 
                                                </p>
                                        `
                                    },
                                ]}
                                footnotes={keynoteFiveNineOhFootnotes}
                            />
                            <TextBlockWrapper>
                                <TextBlock definitions={keynoteFiveNineOhDefinition} >
                                <p><b>ITT (N= 749):</b>  A statistically significant improvement in OS and PFS was demonstrated in patients randomized to KEYTRUDA in combination with chemotherapy compared to chemotherapy.</p>
                                <p><b>CPS &lt; 1 (n=102):</b> An exploratory analysis of OS in the PD-L1 CPS &lt;1 population showed an HR of 0.96 (0.59, 1.55), indicating that the improvement in the ITT population was primarily attributed to the results observed in the subgroup of patients with PD-L1 CPS ≥1.</p>
                            </TextBlock>
                            </TextBlockWrapper>
                            
                            <SecondaryCalloutWrapper>
                                <Heading.H4 as='H3' dangerouslySetInnerHTML={createMarkup(secondaryCalloutHeading)} />
                                <SecondaryCallout
                                   title="38% reduction in the risk of death with KEYTRUDA + chemotherapy vs chemotherapy alone"
                                   alt=""
                                />
                            </SecondaryCalloutWrapper>
                                                       
                            <KMCurve
                                title="Kaplan-Meier curve of OS in patients with PD⁠⁠&#8288;-&#8288;⁠L1 CPS ≥10"
                                image={() => <StaticImage 
                                    placeholder="blurred"
                                    src="../../assets/KEYNOTE-590_KM_Curve_10.png"
                                    alt="KEYNOTE-590: Kaplan-Meier Estimates of OS With KEYTRUDA® (pembrolizumab) + Cisplatin/FU vs Cisplatin/FU Alone in Patients With PD-L1 CPS ≥10" />}
                                hr="HR<sup>a</sup>=0.62 (95% CI, 0.49–0.78; <i>P</i><sup>b</sup><0.0001)"
                                additionalInformation={[
                                    {
                                        // title: 'Additional Information',
                                        text: `
                                            <p>Median OS in months (95% CI):
                                                13.5 (11.1&ndash;15.6) with KEYTRUDA + cisplatin/FU vs 9.4 (8.0&ndash;10.7) with cisplatin/FU alone (HR<sup>a</sup>=0.62; 95% CI, 0.49&ndash;0.78; P<sup>b</sup><0.0001).
                                                </p>
                                            <p>Events observed:
                                                    67% (124/186) with KEYTRUDA + cisplatin/FU vs 84% (165/197) with cisplatin/FU alone. 
                                                </p>
                                        `
                                    },
                                ]}
                                footnotes={esokeynotekmcurvetwo}
                            />
                            <TextBlock>
                                <p><b>CPS&lt;10 (n=347):</b> In an exploratory analysis, in patients with PD-L1 CPS &lt;10, the median OS was 10.5 months (95% CI: 9.7, 13.5) for the KEYTRUDA arm and 10.6 months (95% CI: 8.8, 12.0) for the placebo arm, with a HR of 0.86 (95% CI: 0.68, 1.10).</p>
                            </TextBlock>

                            <ComparisonTable
                               title="Progression-Free Survival in Patients with CPS ≥1"
                                data={
                                    [
                                        [
                                            {
                                                legendTitle: 'KEYTRUDA + cisplatin/FU (n=320)',
                                            },
                                            {
                                                legendTitle: 'Placebo + cisplatin/FU (n=327)',
                                            },
                                        ],
                                        [
                                            {
                                                colspan: 2,
                                                centerText: true,
                                                hrCiP: 'HR<sup>a</sup>=0.62 (95% CI, 0.52-0.73)',
                                            },                                           
                                        ],
                                        [
                                            {
                                                label: 'Number of patients with event (%)',
                                                dataPoint: '252 (79%)',
                                            },
                                            {
                                                label: 'Number of patients with event (%)',
                                                dataPoint: '291 (89%)',
                                            },
                                        ],
                                        [
                                            {
                                                label: 'Median in months (95% CI)',
                                                dataPoint: '6.3 (6.2-7.1)'
                                            },
                                            {
                                                label: 'Median in months (95% CI)',
                                                dataPoint: '5.7 (4.6-6.0)'
                                            },
                                        ],
                                    ]
                                
                                } 
                            />

                            <ComparisonTable
                                title="Progression-Free Survival in Patients with CPS ≥10"
                                data={
                                    [
                                        [
                                            {
                                                legendTitle: 'KEYTRUDA + cisplatin/FU (n=186)',
                                            },
                                            {
                                                legendTitle: 'Placebo + cisplatin/FU (n=197)',
                                            },
                                        ],
                                        [
                                            {
                                                colspan: 2,
                                                centerText: true,
                                                hrCiP: 'HR<sup>a</sup>=0.51 (95% CI, 0.41-0.65; <i>P</i><sup>b</sup><0.0001)'
                                            },                                           
                                        ],
                                        [                                            
                                            {
                                                label: 'Number of patients with event (%)',
                                                dataPoint: '140 (75%)',
                                            },
                                            {
                                                label: 'Number of patients with event (%)',
                                                dataPoint: '174 (88%)',
                                            },
                                        ],
                                        [ 
                                            {
                                                label: 'Median in months (95% CI)',
                                                dataPoint: '7.5 (6.2- 8.2)'
                                            },
                                            {
                                                label: 'Median in months (95% CI)',
                                                dataPoint: '5.5 (4.3-6.0)'
                                            },
                                        ],
                                    ]
                                
                                } 
                            />
                            <TextBlock 
                               footnotes={esokeynotecomparison} >
                                <h4>Objective Response Rate<sup>c</sup> and Duration of Response in Patients with CPS ≥1</h4>
                                <p>ORR (95% CI) was 45% (40–51) with KEYTRUDA + chemo vs 29% (24–34) with chemo alone. There were 19 (6%) complete responses with KEYTRUDA + chemo vs 9 (2.8%) complete responses with chemo alone and 126 (39%) partial responses with KEYTRUDA + chemo vs 85 (26%) partial responses with chemo alone. The median in months (95% CI) for DOR was 8.6 (1.2+to31.0+) months with KEYTRUDA + chemo vs 5.8 (1.5+to25.0+) months with chemo alone.</p>
                                <br />
                                <h4>Objective Response Rate<sup>c</sup> and Duration of Response in Patients with CPS ≥10</h4>
                                <p>ORR (95% CI) was 51% (44–59) with KEYTRUDA + chemo vs 27% (21–34) with chemo alone. There were 11 (6%) complete responses with KEYTRUDA + chemo vs 5 (2.5%) complete responses with chemo alone and 84 (45%) partial responses with KEYTRUDA + chemo vs 48 (24%) partial responses with chemo alone. The median in months (95% CI) for DOR was 10.4 (1.9+ to 28.9+) months with KEYTRUDA + chemo vs 5.6 (1.5+to25.0+) months with chemo alone.</p>                               
                            </TextBlock>

                         </PageSubSection>
                        </PageSection>

                        <PageSection bgColor="cloud" title={anchorLinkModelData[1].label}>
                            <PageSubSection >
                                <ESO3 />
                            </PageSubSection>
                        </PageSection>

                        <PageSection title={anchorLinkModelData[2].label}>
                            <SecondaryCallout 
                                title="First anti–programmed death receptor&#8288;-&#8288;1 (PD&#8288;-&#8288;1) approved in advanced esophageal squamous cell carcinoma (ESCC) (CPS ≥10)"
                                alt=""
                            />
                            <KMCurve
                                title="KEYNOTE&#8288;-&#8288;181: Kaplan-Meier Estimates of Overall Survival (OS) (ESCC CPS ≥10)"
                                limitation="The observed OS hazard ratio in KEYNOTE&#8288;-&#8288;181 was 0.77 (95% CI, 0.63–0.96) in patients with ESCC, 0.70 (95% CI, 0.52–0.94) in patients with tumors expressing PD&#8288;-&#8288;L1 CPS ≥10, and 0.89 (95% CI, 0.75–1.05) in all randomized patients. On further examination in patients whose ESCC tumors expressed PD&#8288;-&#8288;L1 (CPS ≥10), an improvement in OS was observed among patients randomized to KEYTRUDA as compared with chemotherapy."
                                image={() => <StaticImage 
                                    placeholder="blurred"
                                    src="../../assets/eso-181-km-v-2.png"
                                    alt="KEYNOTE-181: Kaplan-Meier Estimates of Overall Survival (OS) (ESCC CPS ≥10) With KEYTRUDA® (pembrolizumab)" />}
                                hr="HR<sup>d</sup>=0.64; 95% CI, 0.46–0.90"
                                footnotes={keynoteOneEightOneFootnotes}
                                definitions="2L = second line."
                            />
                            <ComparisonTable
                                title="KEYNOTE&#8288;-&#8288;181: Overall Survival (OS) (ESCC CPS ≥10)" {...overallSurvivalOneEightOneTable}
                            />
                            <ComparisonTable
                                title="KEYNOTE&#8288;-&#8288;181: Objective Response Rate (ORR) (ESCC CPS ≥10)" {...ObjectiveResponsRatelOneEightOneTable}
                            />
                            <ComparisonTable
                                title="KEYNOTE&#8288;-&#8288;181: Progression-Free Survival (PFS) (ESCC CPS ≥10)" {...progressiveFreeSurvivalOneEightOneTable}
                            />
                            <ComparisonTable
                                title="KEYNOTE&#8288;-&#8288;181: Duration of Response (DOR) (ESCC CPS ≥10)" {...DOR181ComparisonTableData}
                            />

                            <PageSubSection title={anchorLinkModelData[3].label}>
                                <ESO2 />
                            </PageSubSection>
                        </PageSection>

                        <PageSection bgColor="cloud" title="Clinical Findings From KEYNOTE&#8288;-&#8288;180">
                            <TextBlock>
                                <p>The Objective Response Rate in the 35 patients with ESCC expressing PD-L1 was 20% (95% CI: 8 -37). Among the 7 responding patients, the DoR ranged from 4.2 to 25.1+ months, with 5 patients (71%) having responses of 6 months or longer and 3 patients (57%) having responses of 12 months or longer.</p>
                            </TextBlock>

                            <PageSubSection title={anchorLinkModelData[4].label}>
                                <ESO4 />
                            </PageSubSection>

                        </PageSection>                        
                    </TemplateColumn>

                    {/* End Main Column */}
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>

                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent} />
                    </PageSection>

                    {/* <PageSection bgColor="mist">
                        <ReferencesBlock references={pageReferences} />
                    </PageSection> */}

                    
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};

export default Page;
